import { useState } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, FileCheck2, Plus } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PollModal } from '@/components/governance/poll-modal'
import { ResolutionModal } from '@/components/governance/resolution-modal'
import { useAuth } from '@/hooks/use-auth'
import { usePolls } from '@/hooks/use-polls'
import { useResolutions } from '@/hooks/use-resolutions'
import { useTabQueryParam } from '@/hooks/use-tab-query-param'
import { cn, formatDate } from '@/lib/utils'
import { isCommitteeRole } from '@riverpark/types'

const STATUS_BADGE = {
  upcoming: { variant: 'pending', label: 'Upcoming' },
  open: { variant: 'paid', label: 'Open' },
  closed: { variant: 'draft', label: 'Closed' },
} as const

type Tab = 'polls' | 'resolutions'
const TABS: { id: Tab; label: string }[] = [
  { id: 'polls', label: 'Polls' },
  { id: 'resolutions', label: 'Resolutions' },
]
const VALID_TABS: Tab[] = ['polls', 'resolutions']

/** basePath is this section's poll-listing route (e.g. "/admin/polls") — resolutions live as a
 * sibling under the same role prefix, derived from it. */
export function GovernanceWorkspace({ basePath }: { basePath: string }) {
  const [tab, setTab] = useTabQueryParam(VALID_TABS, 'polls')
  const { user } = useAuth()
  const canManage = user ? isCommitteeRole(user.role) : false
  const [showPollModal, setShowPollModal] = useState(false)
  const [showResolutionModal, setShowResolutionModal] = useState(false)

  const resolutionsBasePath = basePath.replace(/\/polls$/, '/resolutions')
  const { data: pollsData, isLoading: pollsLoading } = usePolls({ limit: 50 }, tab === 'polls')
  const { data: resolutionsData, isLoading: resolutionsLoading } = useResolutions(
    { limit: 50 },
    tab === 'resolutions',
  )

  return (
    <PageShell>
      <PageHeader
        label="Governance"
        title="Governance"
        description="AGM votes, resolutions, and estate decisions."
        action={
          canManage ? (
            <Button
              size="sm"
              onClick={() =>
                tab === 'polls' ? setShowPollModal(true) : setShowResolutionModal(true)
              }
            >
              <Plus size={14} className="mr-1.5" />
              {tab === 'polls' ? 'Create poll' : 'Add resolution'}
            </Button>
          ) : undefined
        }
      />

      <div className="flex gap-1 border-b border-border-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-4 py-2.5 font-sans text-sm font-medium transition-colors',
              tab === t.id
                ? 'border-b-2 border-primary text-ink'
                : 'text-muted-foreground hover:text-ink',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'polls' &&
        (pollsLoading ? (
          <p className="font-sans text-sm text-muted-foreground">Loading…</p>
        ) : !pollsData || pollsData.data.length === 0 ? (
          <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
            No polls yet.
          </p>
        ) : (
          <div className="space-y-3">
            {pollsData.data.map((poll) => {
              const badge = STATUS_BADGE[poll.status]
              return (
                <Link
                  key={poll.id}
                  to={`${basePath}/${poll.id}`}
                  className="block rounded-card border border-border-2 bg-card p-4 transition-colors hover:border-primary"
                >
                  <div className="flex items-center gap-2">
                    <BarChart3 size={15} className="shrink-0 text-primary" />
                    <span className="font-serif text-lg text-ink">{poll.title}</span>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </div>
                  <p className="mt-2 font-sans text-xs text-muted-foreground">
                    {poll.authorName} · {formatDate(new Date(poll.createdAt * 1000))} ·{' '}
                    {poll.totalVotes} votes
                  </p>
                </Link>
              )
            })}
          </div>
        ))}

      {tab === 'resolutions' &&
        (resolutionsLoading ? (
          <p className="font-sans text-sm text-muted-foreground">Loading…</p>
        ) : !resolutionsData || resolutionsData.data.length === 0 ? (
          <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
            No resolutions yet.
          </p>
        ) : (
          <div className="space-y-3">
            {resolutionsData.data.map((resolution) => (
              <Link
                key={resolution.id}
                to={`${resolutionsBasePath}/${resolution.id}`}
                className="block rounded-card border border-border-2 bg-card p-4 transition-colors hover:border-primary"
              >
                <div className="flex items-center gap-2">
                  <FileCheck2 size={15} className="shrink-0 text-primary" />
                  <span className="font-serif text-lg text-ink">{resolution.title}</span>
                </div>
                <p className="mt-2 font-sans text-xs text-muted-foreground">
                  {resolution.authorName}
                  {resolution.passedAt &&
                    ` · Passed ${formatDate(new Date(resolution.passedAt * 1000))}`}
                </p>
              </Link>
            ))}
          </div>
        ))}

      {showPollModal && <PollModal onClose={() => setShowPollModal(false)} />}
      {showResolutionModal && <ResolutionModal onClose={() => setShowResolutionModal(false)} />}
    </PageShell>
  )
}
